// ── TemplatePicker ─────────────────────────────────────────────────
//
// Modal that lets the agent pick an APPROVED template to send when
// the 24h session window is closed (must use a template to re-engage).
//
// The caller provides the whatsappAccountId so we can filter to that
// account's templates. On selection the caller receives:
//   { template, variableValues }
// The caller builds the sendMessage payload.

import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Search, X } from 'lucide-react';
import { listTemplates, type WhatsappTemplate } from '../api/whatsapp.api';

function getBodyText(components: any[]): string {
  return components?.find((c) => c.type === 'BODY')?.text ?? '';
}

// Find {{N}} variable placeholders in a text string
function extractVars(text: string): string[] {
  const matches = text.match(/\{\{(\d+)\}\}/g) ?? [];
  // deduplicate preserving order
  const seen = new Set<string>();
  return matches.filter((m) => {
    if (seen.has(m)) return false;
    seen.add(m);
    return true;
  });
}

export interface TemplatePickerResult {
  template: WhatsappTemplate;
  variableValues: Record<string, string>;
}

export function TemplatePicker({
  whatsappAccountId,
  onSelect,
  onClose,
}: {
  whatsappAccountId: string;
  onSelect: (result: TemplatePickerResult) => void;
  onClose: () => void;
}) {
  const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<WhatsappTemplate | null>(null);
  const [varValues, setVarValues] = useState<Record<string, string>>({});
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    listTemplates({ whatsappAccountId, status: 'APPROVED', limit: 50 })
      .then((res) => setTemplates(res.data))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, [whatsappAccountId]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Compute variables when a template is selected
  useEffect(() => {
    if (!selected) { setVarValues({}); return; }
    const bodyText = getBodyText(selected.components);
    const vars = extractVars(bodyText);
    setVarValues(Object.fromEntries(vars.map((v) => [v, ''])));
  }, [selected]);

  const filtered = templates.filter((t) =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()),
  );

  const allVarsFilled =
    selected && Object.values(varValues).every((v) => v.trim().length > 0);
  const hasVars = selected && Object.keys(varValues).length > 0;
  const canSend = selected && (!hasVars || allVarsFilled);

  const doSend = () => {
    if (!selected || !canSend) return;
    onSelect({ template: selected, variableValues: varValues });
  };

  const bodyText = selected ? getBodyText(selected.components) : '';

  // Render body text with variable values substituted
  const previewBody = bodyText.replace(/\{\{(\d+)\}\}/g, (_, n) => {
    const key = `{{${n}}}`;
    return varValues[key] || `{{${n}}}`;
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center">
      <div className="w-full max-w-lg rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="text-sm font-semibold text-slate-800">Send a template message</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!selected ? (
          <>
            {/* Search */}
            <div className="px-4 pt-3 pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <input
                  ref={searchRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search templates…"
                  className="w-full rounded-md border border-gray-200 py-2 pl-8 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              </div>
            </div>

            {/* Template list */}
            <div className="max-h-72 overflow-y-auto px-4 pb-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
                </div>
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-400">
                  {templates.length === 0
                    ? 'No approved templates for this account.'
                    : 'No templates match your search.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {filtered.map((t) => {
                    const body = getBodyText(t.components);
                    return (
                      <button
                        key={t.id}
                        onClick={() => setSelected(t)}
                        className="w-full rounded-lg border border-gray-100 bg-white p-3 text-left hover:border-indigo-200 hover:bg-indigo-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-semibold text-slate-700">
                            {t.name}
                          </span>
                          <span className="text-[10px] text-slate-400">{t.language}</span>
                          <span className="ml-auto text-[10px] font-medium text-slate-400">
                            {t.category}
                          </span>
                        </div>
                        {body && (
                          <p className="mt-1 line-clamp-2 text-xs text-slate-500">{body}</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="px-5 py-4">
            {/* Back */}
            <button
              onClick={() => setSelected(null)}
              className="mb-3 flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800"
            >
              ← Back to templates
            </button>

            {/* Template name */}
            <div className="mb-3 flex items-center gap-2">
              <span className="font-mono text-sm font-semibold text-slate-800">
                {selected.name}
              </span>
              <span className="text-xs text-slate-400">{selected.language}</span>
            </div>

            {/* Variable inputs */}
            {hasVars && (
              <div className="mb-4 space-y-3">
                <p className="text-xs font-medium text-slate-600">Fill in variables</p>
                {Object.keys(varValues).map((varKey) => (
                  <div key={varKey}>
                    <label className="mb-1 block text-xs text-slate-500">
                      {varKey}
                    </label>
                    <input
                      value={varValues[varKey]}
                      onChange={(e) =>
                        setVarValues((prev) => ({ ...prev, [varKey]: e.target.value }))
                      }
                      className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      placeholder={`Value for ${varKey}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Preview */}
            <div className="mb-4 rounded-xl bg-[#e9fbe5] p-3">
              <p className="text-[11px] text-slate-400 mb-1">Preview</p>
              <p className="whitespace-pre-wrap text-[13px] text-slate-800">{previewBody}</p>
            </div>

            <button
              onClick={doSend}
              disabled={!canSend}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:bg-slate-300"
            >
              <CheckCircle2 className="h-4 w-4" />
              Send template
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
